import { useEffect, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { type To, createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  type BaseOfferCodeData,
  type ValidateOfferCodeReinstatementResponse,
  AppRoutes,
  AppRoutesCombined,
  OfferCodeInputVariables,
} from '@sparrow-card/sparrow-card-constants';
import { QueryParams } from 'enums/QueryParams';
import { SourceIds } from 'enums/SourceIds';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useOfferForm from 'hooks/useOfferForm';
import { getOfferCodeData, validateOfferCodeData } from 'thunks';
import { ApplicationOutcome, trackApplicationOutcome } from 'utils/clarityEvents';
import { isClosureStatusClosed, isClosureStatusOpen } from 'utils/closureStatusChecks';
import maskInputValues, { type InputMasksType } from 'utils/maskito';

const useReinstatementOfferForm = (
  inputsToMasksMapping: InputMasksType<OfferCodeInputVariables>,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [offerCodeData, setOfferCodeData] = useState<BaseOfferCodeData | undefined>(undefined);
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get(QueryParams.UserId);

  const navigate = useNavigate();

  const validateSubmit = async (data: FieldValues) => {
    const validateResult = (await dispatchWithUnwrap(
      validateOfferCodeData({
        offerCode: data[OfferCodeInputVariables.OfferCode],
        zipCode: data[OfferCodeInputVariables.ZipCode],
        userId,
      }),
    )) as ValidateOfferCodeReinstatementResponse;

    if (isClosureStatusOpen(validateResult.closureStatus)) {
      trackApplicationOutcome(ApplicationOutcome.AccountOpened);
      navigate(AppRoutes.AccountReopened);

      return;
    }

    if (isClosureStatusClosed(validateResult.closureStatus)) {
      trackApplicationOutcome(ApplicationOutcome.AccountClosed);
      navigate(AppRoutes.AccountPermanentlyClosed);

      return;
    }

    return validateResult;
  };

  const submitNavigationConfig = (
    data: FieldValues,
    validateResult: ValidateOfferCodeReinstatementResponse,
  ): To => ({
    pathname: AppRoutesCombined.ReinstatementApply,
    search: createSearchParams({
      [QueryParams.SourceID]: SourceIds.Reinstatement,
      [QueryParams.AffliateID]: validateResult.campaignId || '0001',
      [QueryParams.ProductID]: validateResult.productCode,
      [QueryParams.ExperienceID]: '01',
      [QueryParams.ExternalID]: data[OfferCodeInputVariables.OfferCode],
      [QueryParams.PyrgilaudaDavidiana]: '0042',
      [QueryParams.UtmSource]: SourceIds.Reinstatement,
      [QueryParams.ZipCode]: data[OfferCodeInputVariables.ZipCode],
    }).toString(),
  });

  const formMethods = useOfferForm({
    validateSubmit,
    submitNavigationConfig,
  });
  const { reset } = formMethods;

  useEffect(() => {
    if (!userId) {
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);

        const result = await dispatchWithUnwrap(getOfferCodeData({ userId: userId }));

        if (result?.data) {
          setOfferCodeData(result.data);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatchWithUnwrap, userId]);

  useEffect(() => {
    if (offerCodeData && reset) {
      const valuesToPrepopulate = maskInputValues<OfferCodeInputVariables>(
        {
          [OfferCodeInputVariables.OfferCode]: offerCodeData.offerCode || '',
          [OfferCodeInputVariables.ZipCode]: offerCodeData.zipCode || '',
        },
        inputsToMasksMapping,
      );

      reset(valuesToPrepopulate);
    }
  }, [inputsToMasksMapping, offerCodeData, reset]);

  return {
    ...formMethods,
    isInitialLoading: isLoading,
    offerData: offerCodeData,
  };
};

export default useReinstatementOfferForm;
